import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Search, X } from 'lucide-react'
import Avatar from '../../../components/Avatar/Avatar'
import UsersRoundCollaboration from '../../../components/ERP/icons/UsersRoundCollaboration'
import { patientService } from '../../../services/patientApi'

function groupByPatient(rows) {
  const groups = []
  rows.forEach((row) => {
    const patientId = row.patient?.id
    if (!patientId) return
    let group = groups.find((item) => item.patient.id === patientId)
    if (!group) {
      group = { patient: row.patient, people: [] }
      groups.push(group)
    }
    if (row.user?.id && !group.people.some((person) => person.id === row.user.id)) {
      group.people.push(row.user)
    }
  })
  return groups.sort((left, right) => String(left.patient.name || '').localeCompare(String(right.patient.name || ''), 'pt'))
}

function ShiftGlanceSheet({ open, onClose }) {
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const listRef = useRef(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (!open) return undefined
    setSearch('')
    setQuery('')
    const onKey = (event) => {
      if (event.key === 'Escape') onCloseRef.current?.()
    }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    let active = true
    setLoading(true)
    setError('')
    patientService.shifts({ status: 'open', search: query })
      .then((response) => {
        if (active) setRows(response.data || [])
      })
      .catch((err) => {
        if (!active) return
        setRows([])
        setError(err.response?.data?.message || 'Não foi possível carregar os plantões')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [open, query])

  useEffect(() => {
    const node = listRef.current
    if (!open || !node) return undefined
    let timer
    const mark = () => {
      node.classList.add('is-scrolling')
      window.clearTimeout(timer)
      timer = window.setTimeout(() => node.classList.remove('is-scrolling'), 700)
    }
    node.addEventListener('scroll', mark, { passive: true })
    return () => {
      node.removeEventListener('scroll', mark)
      window.clearTimeout(timer)
    }
  }, [open, rows.length, loading])

  const groups = useMemo(() => groupByPatient(rows), [rows])

  if (!open) return null

  return createPortal(
    <div className="shift-glance-layer">
      <button type="button" className="shift-glance-overlay" aria-label="Fechar plantões" onClick={onClose} />
      <section className="shift-glance" role="dialog" aria-modal="true" aria-labelledby="shift-glance-title">
        <header className="shift-glance-head">
          <div className="shift-glance-brand">
            <span className="shift-glance-icon" aria-hidden>
              <UsersRoundCollaboration size={20} />
            </span>
            <div>
              <h2 id="shift-glance-title">Plantões abertos</h2>
              <p>Quem está com cada paciente</p>
            </div>
          </div>
          <div className="shift-glance-tools">
            <label className="shift-glance-search">
              <Search size={15} aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar paciente"
                aria-label="Buscar paciente"
              />
            </label>
            <button type="button" className="shift-glance-close" aria-label="Fechar" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </header>
        <div className="shift-glance-list" ref={listRef}>
          {error && <p className="patients-alert-strip">{error}</p>}
          {loading && <p className="patients-muted">Carregando plantões...</p>}
          {!loading && !error && groups.length === 0 && (
            <p className="patients-muted">{query ? 'Nenhum paciente encontrado.' : 'Nenhum plantão aberto.'}</p>
          )}
          {groups.map((group) => (
            <article key={group.patient.id} className="shift-glance-row">
              <div className="shift-glance-patient" title={group.patient.name}>
                <Avatar name={group.patient.name} imageUrl={group.patient.photo_url} size="md" />
                <strong>{group.patient.name}</strong>
              </div>
              <span className="shift-glance-link" aria-hidden>
                <i />
                <span />
                <i />
              </span>
              <div className="shift-glance-staff">
                {group.people.map((person) => (
                  <span key={person.id} className="shift-glance-person" title={person.name || 'Profissional'}>
                    <Avatar
                      name={person.name}
                      email={person.email}
                      imageUrl={person.avatar_url}
                      size="sm"
                    />
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>,
    document.body
  )
}

export default ShiftGlanceSheet
